import { useState, useCallback, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { HustleState, JobInfo, Rarity } from "../types";

interface HustleViewProps {
  data: HustleState | null;
  coins: number;
  loading: boolean;
  onRefresh: () => void;
  onCoinsUpdate: (coins: number) => void;
  onError: (message: string) => void;
}

const RARITY_ACCENT: Record<Rarity, string> = {
  common: "text-muted-foreground",
  uncommon: "text-emerald-400",
  rare: "text-blue-400",
  epic: "text-purple-400",
  legendary: "text-yellow-400",
};

function BrotherRow({
  job,
  onToggle,
  disabled,
  isUpdating,
}: {
  job: JobInfo;
  onToggle: (id: number) => void;
  disabled?: boolean;
  isUpdating?: boolean;
}) {
  const actionLabel = job.isActive ? "Tap to move to bench" : "Tap to add to active team";

  return (
    <button
      type="button"
      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
      onClick={() => onToggle(job.collectionId)}
      disabled={disabled}
      aria-label={`${job.name} - ${actionLabel}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-md bg-muted flex items-center justify-center shrink-0 overflow-hidden border border-border/60">
          {job.image_url ? (
            <img
              src={`/api/img?url=${encodeURIComponent(job.image_url)}`}
              alt={job.name}
              className="w-full h-full object-cover"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <span className={`text-lg font-black ${RARITY_ACCENT[job.rarity]} opacity-70`}>
              {job.name.replace("The ", "").charAt(0)}
            </span>
          )}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold truncate">{job.name}</span>
            {job.variant !== "normal" && (
              <Badge
                className={`text-[8px] h-3.5 px-1 ${
                  job.variant === "shiny"
                    ? "bg-slate-300 text-slate-900"
                    : job.variant === "rainbow"
                      ? "bg-gradient-to-r from-red-400 via-green-400 to-blue-400 text-white"
                      : "bg-green-500 text-black"
                }`}
              >
                {job.variant}
              </Badge>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground truncate">
            {job.jobName} @ {job.company}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs font-medium text-yellow-500">
          +{job.earningsPerTick.toLocaleString()}/tick
        </span>
        <span className="text-[11px] text-muted-foreground hidden sm:inline">
          {isUpdating ? "Updating..." : actionLabel}
        </span>
        <Badge
          variant={job.isActive ? "default" : "outline"}
          className={`text-[10px] ${job.isActive ? "bg-green-600 hover:bg-green-700" : ""}`}
        >
          {job.isActive ? "On Team" : "Bench"}
        </Badge>
      </div>
    </button>
  );
}

async function getErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const err = await res.json() as { error?: string };
    if (typeof err.error === "string" && err.error.trim()) {
      return err.error;
    }
  } catch {
    // Use fallback when response is not JSON
  }
  return fallback;
}

export function HustleView({
  data,
  coins,
  loading,
  onRefresh,
  onCoinsUpdate,
  onError,
}: HustleViewProps) {
  const [clicking, setClicking] = useState(false);
  const [clickFeedback, setClickFeedback] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const clickFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (clickFeedbackTimerRef.current) {
        clearTimeout(clickFeedbackTimerRef.current);
        clickFeedbackTimerRef.current = null;
      }
    };
  }, []);

  const handleClick = useCallback(async () => {
    setClicking(true);
    try {
      const res = await fetch("/api/click", { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        onCoinsUpdate(data.coins);
        setClickFeedback(data.earned);
        if (clickFeedbackTimerRef.current) {
          clearTimeout(clickFeedbackTimerRef.current);
        }
        clickFeedbackTimerRef.current = setTimeout(() => {
          setClickFeedback(null);
          clickFeedbackTimerRef.current = null;
        }, 400);
      } else {
        onError(await getErrorMessage(res, "Could not process click right now."));
      }
    } catch {
      onError("Could not process click right now.");
    } finally {
      setClicking(false);
    }
  }, [onCoinsUpdate, onError]);

  const handleUpgradeClick = useCallback(async () => {
    try {
      const res = await fetch("/api/upgrades/click", { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        onCoinsUpdate(data.coins);
        onRefresh();
      } else {
        onError(await getErrorMessage(res, "Could not upgrade click power."));
      }
    } catch {
      onError("Could not upgrade click power.");
    }
  }, [onCoinsUpdate, onRefresh, onError]);

  const handleToggle = useCallback(
    async (collectionId: number) => {
      if (togglingId !== null) return;
      setTogglingId(collectionId);
      try {
        const res = await fetch("/api/team/toggle", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ collectionId }),
        });
        if (res.ok) {
          onRefresh();
        } else {
          onError(await getErrorMessage(res, "Could not update team right now."));
        }
      } catch {
        onError("Could not update team right now.");
      } finally {
        setTogglingId(null);
      }
    },
    [onRefresh, onError, togglingId]
  );

  const handleUpgradeTeam = useCallback(async () => {
    try {
      const res = await fetch("/api/team/upgrade", { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        onCoinsUpdate(data.coins);
        onRefresh();
      } else {
        onError(await getErrorMessage(res, "Could not upgrade team size."));
      }
    } catch {
      onError("Could not upgrade team size.");
    }
  }, [onCoinsUpdate, onRefresh, onError]);

  if (!data) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-muted-foreground animate-pulse">
          {loading ? "Loading hustle..." : "No hustle data yet."}
        </p>
      </div>
    );
  }

  const clickUpgradeShortfall = Math.max(0, data.clickUpgradeCost - coins);
  const teamUpgradeShortfall = Math.max(0, data.teamUpgradeCost - coins);

  return (
    <div className="flex flex-col gap-6 py-8 px-4 max-w-3xl mx-auto">
      <div className="text-center space-y-1">
        <h2 className="text-2xl font-bold tracking-tight">The Hustle</h2>
        <p className="text-sm text-muted-foreground">
          Click to earn. Put your brothers to work.
        </p>
        {loading && (
          <p className="text-xs text-muted-foreground">Syncing roster...</p>
        )}
      </div>

      {/* Clicker Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Manual Grind</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          <div className="relative">
            <Button
              size="lg"
              className="h-20 w-40 text-lg font-bold active:scale-95 transition-transform"
              onClick={handleClick}
              disabled={clicking}
            >
              CLICK
            </Button>
            {clickFeedback !== null && (
              <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-yellow-400 font-bold text-sm animate-bounce pointer-events-none">
                +{clickFeedback.toLocaleString()}
              </span>
            )}
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                Power: <span className="font-semibold text-foreground">{data.clickPower.toLocaleString()}</span>
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleUpgradeClick}
                disabled={coins < data.clickUpgradeCost}
              >
                Upgrade ({data.clickUpgradeCost.toLocaleString()} coins)
              </Button>
            </div>
            {clickUpgradeShortfall > 0 && (
              <p className="text-xs text-muted-foreground">
                Need {clickUpgradeShortfall.toLocaleString()} more coins to upgrade.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Passive Income Summary */}
      <Card>
        <CardContent className="flex items-center justify-between py-4">
          <div>
            <p className="text-sm font-medium">Passive Income</p>
            <p className="text-xs text-muted-foreground">Every 10 seconds</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-yellow-500">+{data.coinsPerTick.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">
              coins/tick
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Active Team */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold">
            Active Team ({data.activeTeam.length}/{data.teamSize})
          </h3>
          <div className="flex flex-col items-end gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={handleUpgradeTeam}
              disabled={coins < data.teamUpgradeCost}
            >
              +1 Slot ({data.teamUpgradeCost.toLocaleString()} coins)
            </Button>
            {teamUpgradeShortfall > 0 && (
              <p className="text-xs text-muted-foreground">
                Need {teamUpgradeShortfall.toLocaleString()} more coins.
              </p>
            )}
          </div>
        </div>
        {data.activeTeam.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No brothers on the team yet. Add some from the bench below.
          </p>
        ) : (
          <div className="space-y-2">
            {data.activeTeam.map((job) => (
              <BrotherRow
                key={job.collectionId}
                job={job}
                onToggle={handleToggle}
                disabled={togglingId !== null}
                isUpdating={togglingId === job.collectionId}
              />
            ))}
          </div>
        )}
      </div>

      {/* Bench */}
      <div>
        <h3 className="text-base font-semibold mb-3">
          Bench ({data.bench.length})
        </h3>
        <p className="text-xs text-muted-foreground mb-2">
          Tap a brother to add them to your active team.
        </p>
        {data.bench.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            Open packs to unlock brothers and put them to work.
          </p>
        ) : (
          <div className="space-y-2">
            {data.bench.map((job) => (
              <BrotherRow
                key={job.collectionId}
                job={job}
                onToggle={handleToggle}
                disabled={togglingId !== null}
                isUpdating={togglingId === job.collectionId}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
